import { useState, useEffect, useRef } from 'react';
import { Phone, X, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/lib/utils';
import { trackLead } from '@/lib/track';
import { getAttribution } from '@/lib/attribution';

const FloatingCallbackCTA = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Listen for FAB menu event
  useEffect(() => {
    const handler = () => { setVisible(true); setExpanded(true); };
    window.addEventListener('open-callback', handler);
    return () => window.removeEventListener('open-callback', handler);
  }, []);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const panelRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 25 && !dismissed) {
        setVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [dismissed]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expanded && panelRef.current && !panelRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error('Please enter your name and phone number');
      return;
    }

    setSubmitting(true);
    try {
      const { data: createdLead } = await axios.post(`${API}/leads`, {
        name: name.trim(),
        phone: phone.trim(),
        email: '',
        message: '[Floating Callback CTA] Requested a callback',
        lead_type: 'callback_request',
        source_page: window.location.pathname,
        attribution: getAttribution()
      });
      trackLead('callback_request', createdLead?.id, { form_position: 'floating_callback' });
      setSubmitted(true);
      toast.success('We\'ll call you shortly!');
    } catch (error) {
      console.error('Error submitting callback:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
    setExpanded(false);
  };

  if (!visible || dismissed) return null;

  return (
    <div
      ref={panelRef}
      data-testid="floating-callback-cta"
      className={`fixed left-0 top-1/2 -translate-y-1/2 z-50 transition-all duration-500 ease-out hidden md:block ${
        visible ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      {!expanded ? (
        <div className="relative group">
          <button
            data-testid="callback-cta-trigger"
            onClick={() => setExpanded(true)}
            className="flex items-center bg-navy hover:bg-navy/95 text-white rounded-r-xl shadow-xl transition-all duration-300 overflow-hidden"
          >
            <div className="flex items-center gap-2 px-3 py-3">
              <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Phone className="w-4 h-4 text-navy" />
              </div>
              <span className="text-sm font-medium whitespace-nowrap pr-1">We&apos;ll call you</span>
            </div>
          </button>
          <button
            data-testid="callback-cta-dismiss"
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white/90 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity z-10"
          >
            <X className="w-3 h-3 text-navy" />
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-r-2xl shadow-2xl border border-gray-100 w-72 overflow-hidden animate-in slide-in-from-left duration-300">
          {/* Header */}
          <div className="bg-navy px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gold rounded-full flex items-center justify-center">
                <Phone className="w-3.5 h-3.5 text-navy" />
              </div>
              <span className="text-white font-medium text-sm">Request a Callback</span>
            </div>
            <button
              data-testid="callback-panel-close"
              onClick={() => setExpanded(false)}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {submitted ? (
              <div data-testid="callback-success" className="text-center py-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <p className="font-semibold text-navy text-sm">We&apos;ll call you soon!</p>
                <p className="text-muted-foreground text-xs mt-1">
                  Typically within 30 minutes during business hours.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDismiss}
                  className="mt-3 text-xs"
                >
                  Close
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Leave your number and we&apos;ll call you back — no pressure, just answers.
                </p>
                <Input
                  data-testid="callback-cta-name"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-9 text-sm"
                />
                <Input
                  data-testid="callback-cta-phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-9 text-sm"
                />
                <Button
                  data-testid="callback-cta-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full h-9 bg-gold text-navy hover:bg-gold-hover text-sm font-semibold"
                >
                  {submitting ? 'Sending...' : 'Call Me Back'}
                  {!submitting && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
                </Button>
                <p className="text-[10px] text-muted-foreground text-center">
                  No spam. We typically respond within 30 min.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingCallbackCTA;
